import { useContract } from '@hooks/essential-ui/UseContract';
import { useIsAdminUser } from '@hooks/essential-ui/UseIsAdminUser';
import { useSetContract } from '@hooks/essential-ui/UseSetContract';
import { useUserDealer } from '@hooks/UseUserDealer';
import { PutUsingQueryParameters } from '@services/api/GenericClient';
import { ErrorToast, SuccessToast } from '@services/utils/ToastUtils';
import { AppDispatch } from '@store/AppState';
import { getOffers } from '@store/essential-ui/offers/OffersSlice';
import { theme } from '@theme';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import ModalTitle from '../ModalTitle';
import ContractDetails from './ContractDetails';
import { ContractModalPlaceholder } from './ContractModalPlaceholder';
import { ModalFooter, ModalReactivateButton } from './Style';

type ContractModalProps = {
  modalIsOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  closedDealId: string;
  chromeId?: string;
  priorLeaseId?: string | null | undefined;
  dealerId: string;
};

const ContractModal: FC<ContractModalProps> = (props) => {
  useSetContract(props.closedDealId, props.chromeId, props.dealerId);
  const { contract, loading } = useContract();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdminUser } = useIsAdminUser();
  const [contractTitle, setContractTitle] = useState('');
  const { isIncentiveAdvantageType } = useUserDealer();
  const isLeaseBuyout =
    props.priorLeaseId !== undefined &&
    props.priorLeaseId !== null &&
    props.priorLeaseId !== '00000000-0000-0000-0000-000000000000';

  useEffect(() => {
    if (!loading && contract) {
      setContractTitle(
        isLeaseBuyout
          ? 'Contract History'
          : `${contract.dealType} Contract For ${contract.buyerFullName}`,
      );
    }
  }, [loading, contract, isLeaseBuyout]);

  const handleCloseContractModal = () => {
    props.setIsOpen(false);
  };

  const isEligibleForReactivate = () => {
    if (contract && contract.amVehicleId && props.closedDealId && props.chromeId) {
      return false;
    } else {
      return true;
    }
  };

  const reActivateDeal = async () => {
    const url = `/common/dealstatus?vin=${contract?.vin}&closedDealId=${props.closedDealId}&isDealActive=${true}&dealerId=${props.dealerId}`;
    try {
      const response = await PutUsingQueryParameters(url);
      handleCloseContractModal();
      SuccessToast('Deal Status Updated.');
      dispatch(
        getOffers({ showPublicOffers: !isIncentiveAdvantageType, isAdminUser: isAdminUser }),
      );
    } catch (exception) {
      ErrorToast(`An error has occurred while reactivating deal.`);
    }
  };

  return (
    <div data-testid='contract-modal'>
      <Modal
        isOpen={props.modalIsOpen}
        onRequestClose={handleCloseContractModal}
        style={modalStyles}
        contentLabel='Contract Modal'
        ariaHideApp={false}
      >
        <ModalContent>
          <ModalTitle
            bgColor={theme.palette.text.purple}
            title={contractTitle}
            size={theme.typography.h4}
            color={theme.palette.white}
            fontWeight={theme.typography.fontWeights.semibold}
            iconColor={theme.palette.white}
            handleIconClick={handleCloseContractModal}
            showCloseIcon={true}
          />

          {!loading && contract && (
            <ContractDetails
              data={contract}
              priorLeaseId={props.priorLeaseId}
              dealerId={props.dealerId}
            />
          )}
          {(loading || !contract) && showPlaceholders()}
          {props.closedDealId && props.chromeId && (
            <ModalFooter>
              <ModalReactivateButton
                className='btn'
                type='button'
                onClick={reActivateDeal}
                value='REACTIVATE DEAL'
                disabled={isEligibleForReactivate()}
              />
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ContractModal;

const showPlaceholders = () => <ContractModalPlaceholder />;

const modalStyles = {
  content: {
    top: '60px',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, 2%)',
    width: '1000px',
    minHeight: '300px',
    maxHeight: '640px',
    zIndex: 100000,
    padding: '0',
  },
};

const ModalContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

export const LoadingWrapper = styled.div`
  top: 50%;
  left: 50%;
  display: flex;
  flex-direction: column;
  margin: auto;
  width: 500px;
  height: 300px;
  gap: 30px;
  padding: 0;
  z-index: 1000;
`;
